import React, { useState, useEffect, useContext } from "react";
import { useParams, useHistory } from "react-router-dom";

import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import IconButton from "@material-ui/core/IconButton";
import SearchIcon from "@material-ui/icons/Search";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import {
  Grid,
  Chip,
  Tooltip,
  Menu,
  MenuItem,
  Typography,
  Box,
  Tabs,
  Tab,
} from "@material-ui/core";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import MoreVertIcon from "@material-ui/icons/MoreVert";
import RefreshIcon from "@material-ui/icons/Refresh";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import CancelIcon from "@material-ui/icons/Cancel";
import EmailIcon from "@material-ui/icons/Email";
import PhoneIcon from "@material-ui/icons/Phone";
import LinkedInIcon from "@material-ui/icons/LinkedIn";

import MainContainer from "../../../components/MainContainer";
import MainHeader from "../../../components/MainHeader";
import Title from "../../../components/Title";

import { i18n } from "../../../translate/i18n";
import TableRowSkeleton from "../../../components/TableRowSkeleton";
import toastError from "../../../errors/toastError";
import toastUtils from "../../../utils/toastUtils";
import { AuthContext } from "../../../context/Auth/AuthContext";

import * as sdrService from "../../../services/sdr";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  statusChip: {
    fontSize: "0.75rem",
  },
  scoreChip: {
    fontWeight: "bold",
  },
  campaignHeader: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    backgroundColor: theme.palette.background.default,
  },
  tabsContainer: {
    borderBottom: `1px solid ${theme.palette.divider}`,
    marginBottom: theme.spacing(2),
  },
}));

const SDRLeads = () => {
  const classes = useStyles();
  const { campaignId } = useParams();
  const history = useHistory();
  const { user } = useContext(AuthContext);

  const [loading, setLoading] = useState(false);
  const [campaign, setCampaign] = useState(null);
  const [leads, setLeads] = useState([]);
  const [searchParam, setSearchParam] = useState("");
  const [tabValue, setTabValue] = useState(0);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedLead, setSelectedLead] = useState(null);

  useEffect(() => {
    fetchCampaign();
    fetchLeads();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignId]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchLeads();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParam, tabValue]);

  const fetchCampaign = async () => {
    try {
      const { data } = await sdrService.getCampaign(campaignId);
      setCampaign(data.data);
    } catch (err) {
      toastError(err);
    }
  };

  const fetchLeads = async () => {
    try {
      setLoading(true);
      const statusFilter = getStatusFilter();
      const { data } = await sdrService.getLeads(campaignId, {
        searchParam,
        status: statusFilter,
      });
      setLeads(data.leads || []);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusFilter = () => {
    switch (tabValue) {
      case 0:
        return null; // All
      case 1:
        return "qualified";
      case 2:
        return "contacted";
      case 3:
        return "responded";
      case 4:
        return "disqualified";
      default:
        return null;
    }
  };

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleMenuOpen = (event, lead) => {
    setAnchorEl(event.currentTarget);
    setSelectedLead(lead);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedLead(null);
  };

  const handleRequalify = async () => {
    if (!selectedLead) return;

    try {
      await sdrService.requalifyLead(selectedLead.id);
      toastUtils.success(i18n.t("sdr.leads.toasts.requalified"));
      fetchLeads();
    } catch (err) {
      toastError(err);
    }
    handleMenuClose();
  };

  const handleDelete = async () => {
    if (!selectedLead) return;

    try {
      await sdrService.deleteLead(selectedLead.id);
      toastUtils.success(i18n.t("sdr.leads.toasts.deleted"));
      fetchLeads();
    } catch (err) {
      toastError(err);
    }
    handleMenuClose();
  };

  const getQualificationColor = (score) => {
    if (score >= 80) return "primary";
    if (score >= 60) return "secondary";
    if (score >= 40) return "default";
    return "default";
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "qualified":
        return "primary";
      case "contacted":
        return "secondary";
      case "responded":
        return "primary";
      case "disqualified":
        return "default";
      default:
        return "default";
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("pt-BR");
  };

  return (
    <MainContainer>
      <MainHeader>
        <Grid style={{ width: "99.6%" }} container alignItems="center">
          <Grid xs={12} sm={8} item>
            <Box display="flex" alignItems="center">
              <Tooltip title={i18n.t("common.back")}>
                <IconButton onClick={() => history.goBack()} size="small">
                  <ArrowBackIcon />
                </IconButton>
              </Tooltip>
              <Title>{campaign?.name || i18n.t("sdr.leads.title")}</Title>
            </Box>
          </Grid>
          <Grid xs={12} sm={4} item>
            <Grid spacing={2} container>
              <Grid xs={7} sm={6} item>
                <TextField
                  fullWidth
                  placeholder={i18n.t("sdr.leads.searchPlaceholder")}
                  type="search"
                  value={searchParam}
                  onChange={handleSearch}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon style={{ color: "gray" }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid xs={5} sm={6} item>
                <Button
                  fullWidth
                  variant="contained"
                  color="primary"
                  onClick={fetchLeads}
                  startIcon={<RefreshIcon />}
                >
                  {i18n.t("common.refresh")}
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </MainHeader>

      {campaign && (
        <Paper className={classes.campaignHeader}>
          <Typography variant="subtitle1" gutterBottom>
            {campaign.description}
          </Typography>
          <Box display="flex" gap={2}>
            <Chip
              label={`${i18n.t("sdr.leads.total")}: ${campaign.totalProspects || 0}`}
              color="primary"
              variant="outlined"
            />
            <Chip
              label={`${i18n.t("sdr.leads.qualified")}: ${campaign.qualifiedLeads || 0}`}
              color="secondary"
              variant="outlined"
            />
            <Chip
              label={`${i18n.t("sdr.leads.contacted")}: ${campaign.contactedLeads || 0}`}
              color="default"
              variant="outlined"
            />
          </Box>
        </Paper>
      )}

      <Paper className={classes.tabsContainer}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
        >
          <Tab label={i18n.t("sdr.leads.tabs.all")} />
          <Tab label={i18n.t("sdr.leads.tabs.qualified")} />
          <Tab label={i18n.t("sdr.leads.tabs.contacted")} />
          <Tab label={i18n.t("sdr.leads.tabs.responded")} />
          <Tab label={i18n.t("sdr.leads.tabs.disqualified")} />
        </Tabs>
      </Paper>

      <Paper className={classes.mainPaper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{i18n.t("sdr.leads.table.name")}</TableCell>
              <TableCell align="center">{i18n.t("sdr.leads.table.company")}</TableCell>
              <TableCell align="center">{i18n.t("sdr.leads.table.title")}</TableCell>
              <TableCell align="center">{i18n.t("sdr.leads.table.contact")}</TableCell>
              <TableCell align="center">{i18n.t("sdr.leads.table.score")}</TableCell>
              <TableCell align="center">{i18n.t("sdr.leads.table.status")}</TableCell>
              <TableCell align="center">{i18n.t("sdr.leads.table.lastContact")}</TableCell>
              <TableCell align="center">{i18n.t("sdr.leads.table.actions")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <>
              {loading && <TableRowSkeleton columns={8} />}
              {!loading &&
                leads.map((lead) => (
                  <TableRow key={lead.id}>
                    <TableCell>
                      {lead.firstName} {lead.lastName}
                    </TableCell>
                    <TableCell align="center">
                      {lead.companyName || "-"}
                    </TableCell>
                    <TableCell align="center">{lead.title || "-"}</TableCell>
                    <TableCell align="center">
                      <Box display="flex" justifyContent="center" gap={0.5}>
                        {lead.email && (
                          <Tooltip title={lead.email}>
                            <EmailIcon fontSize="small" color="action" />
                          </Tooltip>
                        )}
                        {lead.phone && (
                          <Tooltip title={lead.phone}>
                            <PhoneIcon fontSize="small" color="action" />
                          </Tooltip>
                        )}
                        {lead.linkedinUrl && (
                          <Tooltip title={i18n.t("sdr.leads.linkedin")}>
                            <LinkedInIcon fontSize="small" color="action" />
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={lead.qualificationScore || 0}
                        color={getQualificationColor(lead.qualificationScore)}
                        size="small"
                        className={classes.scoreChip}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={i18n.t(`sdr.leads.status.${lead.qualificationStatus}`)}
                        color={getStatusColor(lead.qualificationStatus)}
                        size="small"
                        className={classes.statusChip}
                      />
                    </TableCell>
                    <TableCell align="center">
                      {formatDate(lead.lastContactedAt)}
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuOpen(e, lead)}
                      >
                        <MoreVertIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              {!loading && leads.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography variant="body2" color="textSecondary">
                      {i18n.t("sdr.leads.noLeads")}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </>
          </TableBody>
        </Table>
      </Paper>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleRequalify}>
          <RefreshIcon fontSize="small" style={{ marginRight: 8 }} />
          {i18n.t("sdr.leads.actions.requalify")}
        </MenuItem>
        <MenuItem onClick={handleDelete}>
          <CancelIcon fontSize="small" style={{ marginRight: 8 }} />
          {i18n.t("common.delete")}
        </MenuItem>
      </Menu>
    </MainContainer>
  );
};

export default SDRLeads;
